import { useState } from 'react'
import { X } from 'lucide-react'
import { TChatHistory } from 'extra/types/Messages'
import { IAPISimulationItem } from 'extra/types/ApiModels'
import { JSONHelper } from 'extra/Helpers/JSONHelper'
import { api } from '../../api/client'
import { ChatHistoryEditor } from '../../components/ChatHistoryEditor'

export function SimulationItemEditor ({
  suiteId,
  item,
  onSave,
  onClose,
}: {
  suiteId: string
  item: IAPISimulationItem | null
  onSave: (item: IAPISimulationItem) => void
  onClose: () => void
}) {
  const [title, setTitle] = useState(item?.title || '')
  const [driverInstructions, setDriverInstructions] = useState(item?.driverInstructions || '')
  const [toolInstructions, setToolInstructions] = useState(item?.toolInstructions || '')
  const [variablesJson, setVariablesJson] = useState(
    item?.variablesValues ? JSONHelper.unescapeNewlinesDeep(JSON.stringify(item.variablesValues, null, 2)) : '{}',
  )
  const [criticalCriteria, setCriticalCriteria] = useState(
    item?.evalCriteria?.critical?.join('\n') || '',
  )
  const [niceToHaveCriteria, setNiceToHaveCriteria] = useState(
    item?.evalCriteria?.niceToHave?.join('\n') || '',
  )
  const [initialChatHistory, setInitialChatHistory] = useState<TChatHistory>(
    item?.initialChatHistory || [],
  )

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSave = async () => {
    setSaving(true)
    setError(null)

    try {
      let variablesValues: Record<string, any> | undefined
      try {
        variablesValues = JSON.parse(JSONHelper.escapeNewlinesInsideJsonStrings(variablesJson))
      } catch {
        setError('Invalid JSON in variables')
        setSaving(false)
        return
      }

      const criticalArr = criticalCriteria.split('\n').map(s => s.trim()).filter(Boolean)
      const niceToHaveArr = niceToHaveCriteria.split('\n').map(s => s.trim()).filter(Boolean)
      const evalCriteria = (criticalArr.length > 0 || niceToHaveArr.length > 0)
        ? { critical: criticalArr, niceToHave: niceToHaveArr }
        : undefined

      const data = {
        title: title || undefined,
        driverInstructions: driverInstructions || undefined,
        variablesValues,
        initialChatHistory: initialChatHistory.length > 0 ? initialChatHistory : null,
        toolInstructions: toolInstructions || undefined,
        evalCriteria,
      }

      let saved: IAPISimulationItem
      if (item) {
        saved = await api.simulations.updateItem(item.id, data)
      } else {
        saved = await api.simulations.createItem(suiteId, data)
      }
      onSave(saved)
    } catch (e) {
      setError('Failed to save item')
      console.error(e)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h3 className="text-lg font-medium">
            {item ? 'Edit Simulation Item' : 'New Simulation Item'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white cursor-pointer">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {error && <div className="alert-error">{error}</div>}

          <div>
            <label className="input-label">Title</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              className="input"
              placeholder="Optional title for this test case"
            />
          </div>

          <div>
            <label className="input-label">Driver Instructions</label>
            <textarea
              value={driverInstructions}
              onChange={e => setDriverInstructions(e.target.value)}
              className="input"
              rows={4}
              placeholder="Instructions for the driver LLM. E.g.: You are a frustrated customer who wants a refund for order #12345. When the agent asks for details, provide order number and amount ($49.99)."
            />
          </div>

          <div>
            <label className="input-label">Tool Instructions</label>
            <textarea
              value={toolInstructions}
              onChange={e => setToolInstructions(e.target.value)}
              className="input"
              rows={4}
              placeholder="Instructions for how to respond to tool calls. E.g.: When the agent calls get_order, return {status: 'delivered', amount: 49.99}. When it calls process_refund, return {success: true}."
            />
          </div>

          <div>
            <label className="input-label">Initial Chat History</label>
            <ChatHistoryEditor
              history={initialChatHistory}
              onChange={setInitialChatHistory}
              defaultRole="user"
              minHeight="min-h-[40px]"
            />
          </div>

          <div>
            <label className="input-label">Variables (JSON)</label>
            <textarea
              value={variablesJson}
              onChange={e => setVariablesJson(e.target.value)}
              className="input font-mono text-xs"
              rows={3}
            />
          </div>

          <div>
            <label className="input-label">Critical Eval Criteria (one per line)</label>
            <textarea
              value={criticalCriteria}
              onChange={e => setCriticalCriteria(e.target.value)}
              className="input"
              rows={3}
              placeholder="Agent must look up the order&#10;Agent must process the refund"
            />
          </div>

          <div>
            <label className="input-label">Nice-to-Have Eval Criteria (one per line)</label>
            <textarea
              value={niceToHaveCriteria}
              onChange={e => setNiceToHaveCriteria(e.target.value)}
              className="input"
              rows={3}
              placeholder="Agent apologizes for the inconvenience&#10;Agent confirms refund amount"
            />
          </div>

        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-gray-700">
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} className="btn btn-primary">
            {saving ? 'Saving...' : (item ? 'Update' : 'Create')}
          </button>
        </div>
      </div>
    </div>
  )
}
